import { useEffect, useState } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import type { ReactElement } from 'react'
import type { IStateReducer } from '~/types/analytics-context'

import CohortedAnalyticsRouter from './CohortedAnalytics/Router'
import AnalyticsFilters from './Filters'
import BusinessReportRouter from './Reporting/Router'

import Header from '~/components/Header/Classic'
import { SESSION_STORAGE } from '~/const'
import { AnalyticsContextProvider, useAnalyticsContext } from '~/providers/Analytics'
import { useAppSummary } from '~/providers/AppSummary'
import { transformByDeviceType } from '~/utils/application'

export const caDefaultFilterDisplay: IStateReducer['displayedFilters'] = {
  datePicker: true,
  dropdownAppKey: false,
  dropdownBreakdown: true,
  dropdownCountry: false,
  dropdownDai: true,
  dropdownDeviceCategory: true,
  dropdownDeviceType: false,
  dropdownInstallationAppVersion: true,
  dropdownInstallationCountry: true,
  dropdownUaSource: true,
  dropdownUserType: true,
}

export const businessReportsDefaultFilterDisplay: IStateReducer['displayedFilters'] = {
  datePicker: true,
  dropdownAppKey: true,
  dropdownBreakdown: true,
  dropdownCountry: true,
  dropdownDai: false,
  dropdownDeviceCategory: false,
  dropdownDeviceType: true,
  dropdownInstallationAppVersion: false,
  dropdownInstallationCountry: false,
  dropdownUaSource: false,
  dropdownUserType: false,
}

const legacyBrDisplayedFilters = {
  datePicker: true,
  dropdownAppKey: false,
  dropdownBreakdown: false,
  dropdownCountry: true,
  dropdownDai: false,
  dropdownDeviceCategory: false,
  dropdownDeviceType: false,
  dropdownInstallationAppVersion: false,
  dropdownInstallationCountry: false,
  dropdownUaSource: false,
  dropdownUserType: false,
}

const displayedFilters: Record<
  'analytics-aggregated' | 'business-reports',
  Record<string, IStateReducer['displayedFilters']>
> = {
  'analytics-aggregated': {
    'app-usage': caDefaultFilterDisplay,
    builder: caDefaultFilterDisplay,
    community: caDefaultFilterDisplay,
    crash: caDefaultFilterDisplay,
    favourites: caDefaultFilterDisplay,
    'metrics-overview': {
      ...caDefaultFilterDisplay,
      dropdownBreakdown: false,
    },
    'monet-ads': caDefaultFilterDisplay,
    'monet-iap': caDefaultFilterDisplay,
    overview: caDefaultFilterDisplay,
    'publishing-overview': caDefaultFilterDisplay,
    sandbox: caDefaultFilterDisplay,
    stability: caDefaultFilterDisplay,
    usage: caDefaultFilterDisplay,
  },
  'business-reports': {
    overview: businessReportsDefaultFilterDisplay,
    sandbox: businessReportsDefaultFilterDisplay,
  },
}

interface Props {
  withEvents: boolean
}

const Router = ({ withEvents }: Props) => {
  /** Local state */

  const { pathname } = useLocation()

  const { dispatch } = useAnalyticsContext()

  const { appDetails, deviceType, isError } = useAppSummary()

  const appSelectorApp = appDetails ? transformByDeviceType(appDetails, deviceType) : undefined

  const isSwitchDisabled = !appDetails?.android || !appDetails?.ios

  /** Effects */

  useEffect(() => {
    if (pathname.includes('business-report/legacy')) {
      dispatch({
        payload: 'business_reports',
        type: 'SET_FEATURE',
      })
      dispatch({
        payload: legacyBrDisplayedFilters,
        type: 'SET_DISPLAYED_FILTERS',
      })
    } else if (pathname.includes('ca')) {
      dispatch({
        payload: 'analytics_aggregated',
        type: 'SET_FEATURE',
      })
      dispatch({
        payload: caDefaultFilterDisplay,
        type: 'SET_DISPLAYED_FILTERS',
      })
    } else {
      dispatch({
        payload: 'business_reports',
        type: 'SET_FEATURE',
      })
      dispatch({
        payload: businessReportsDefaultFilterDisplay,
        type: 'SET_DISPLAYED_FILTERS',
      })
    }
  }, [dispatch, pathname])

  /** Render */

  return (
    <>
      <Header
        apps={appSelectorApp ? [appSelectorApp] : []}
        isAppSelectorOpen={isError}
        isSwitchDisabled={isSwitchDisabled}
      />
      <Routes>
        <Route element={<BusinessReportRouter />} path="business-report/*" />
        <Route element={<CohortedAnalyticsRouter withEvents={withEvents} />} path="ca/*" />
      </Routes>
    </>
  )
}

const AnalyticsRouter = (): ReactElement => {
  /** Local state */

  const withEventForSession = sessionStorage.getItem(SESSION_STORAGE.SHOW_EVENTS_ON_CHARTS)

  const [withEvents, setWithEvents] = useState(!!withEventForSession ? withEventForSession === 'true' : true)

  /** Handlers */

  const handleWithEventsChange = (value: boolean) => setWithEvents(value)

  /** Render */

  return (
    <div className="view-layout analytics-router">
      <AnalyticsContextProvider displayedFilters={displayedFilters}>
        <AnalyticsFilters onWithEventsChange={handleWithEventsChange} />
        <Router withEvents={withEvents} />
      </AnalyticsContextProvider>
    </div>
  )
}

export default AnalyticsRouter
